import React from "react";
import Stripe from "stripe";
import { differenceInDays } from "date-fns";
import { useQuery, useQueryClient } from "react-query";
import api, { queryFetcher } from "../src/APIResolvers";
import { useAuthContext } from "./AmplifyContext";
import CognitoClient from "../lib/aws/cognito";
// import { useAuthContext } from './AmplifyContext';

const StripeSubscriptionContext = React.createContext<{
  subscription: (Stripe.Subscription & { plan: Stripe.Plan }) | undefined;
  refetchSubscription: () => void;
  isSubscriptionLoading: boolean;
  isTrialExpired: boolean;
  subscriptionError: any;
}>({
  subscription: undefined,
  refetchSubscription: () => {},
  isSubscriptionLoading: true,
  isTrialExpired: false,
  subscriptionError: null,
});

function StripeSubscriptionContextProvider<T extends object>(
  props: React.PropsWithChildren<T>,
): React.ReactElement {
  const queryClient = useQueryClient();
  const { authedUser } = useAuthContext();
  const [isTrialExpired, setIsTrialExpired] = React.useState(false);

  React.useEffect(() => {
    if (authedUser) {
      const getDateRegistered = async () => {
        const diff = await CognitoClient.adminGetUser({
          UserPoolId: process.env.NEXT_PUBLIC_AMPLIFY_USERPOOL_ID as string,
          Username: authedUser.username,
        }).then((res) => differenceInDays(new Date(), res.UserCreateDate as Date));

        setIsTrialExpired(diff > 30);
      };

      getDateRegistered();
    }
  }, [authedUser]);

  const {
    data: subscription,
    error: subscriptionError,
    isLoading: isSubscriptionLoading,
  } = useQuery(
    "subscription",
    () =>
      queryFetcher<Stripe.Subscription & { plan: Stripe.Plan }>(
        api.settings.ENDPOINTS.subscription(),
      ),
    { enabled: false /*isTrialExpired*/, retry: 1 },
  );

  const value = React.useMemo(
    () => ({
      subscription,
      refetchSubscription: () => queryClient.invalidateQueries("subscription"),
      isSubscriptionLoading,
      subscriptionError,
      isTrialExpired,
    }),
    [isSubscriptionLoading, isTrialExpired, queryClient, subscription, subscriptionError],
  );

  return <StripeSubscriptionContext.Provider value={value} {...props} />;
}

function useStripeSubscription() {
  const context = React.useContext(StripeSubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useStripeSubscription must be used within an StripeSubscriptionContextProvider",
    );
  }
  return context;
}

export { StripeSubscriptionContextProvider, useStripeSubscription, StripeSubscriptionContext };
